import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Button, Form, Spinner } from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import { getIndexerErrorInfo } from '../api/errors';
import { indexerAlertVariant } from '../lib/indexer-alert-variant';
import {
  useCreateIndexScheduleMutation,
  useDeleteIndexScheduleMutation,
  useLazyListIndexSchedulesQuery,
  usePatchIndexScheduleMutation,
} from '../api/indexerApi';
import type {
  IndexScheduleItem,
  ProjectJobState,
  ScheduleMessenger,
  WebhookEventCatalogItem,
} from '../api/types';
import { jobStateLabel } from '../lib/job-labels';
import { ScheduleListTable } from './ScheduleListTable';
import { SchedulePanelPreload } from './preloads/IndexerPreloads';
import { ScheduleDeleteConfirmModal } from './ScheduleDeleteConfirmModal';
import {
  DEFAULT_SCHEDULE_MESSENGERS,
  ScheduleMessengerSelect,
  filterJoinedScheduleMessengers,
  formatScheduleMessengersLabel,
} from './ScheduleMessengerSelect';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { ScheduleWebhookEventsForm } from './ScheduleWebhookEventsForm';
import {
  crossScheduleWarnings,
  findDuplicateLines,
  findNonXmlSitemapUrls,
  normalizeLines,
} from '../lib/schedule-duplicates';
import { formatFaNumber, formatTime, projectSitemapListPlaceholder } from '../lib/utils';
import { useProject } from '../lib/project-context';
import { cleanupOrphanModalBackdrops } from '../lib/modal-body-cleanup';

function formatRequestError(err: unknown, fallback: string): string {
  const info = getIndexerErrorInfo(err);
  return `HTTP ${info.code} — ${info.message || fallback}`;
}

export function IndexSchedulePanel({ projectId }: { projectId: string }) {
  const { project } = useProject();
  const { data: userInfo } = useGetUserInfoQuery();
  const [schedules, setSchedules] = useState<IndexScheduleItem[]>([]);
  const [catalog, setCatalog] = useState<WebhookEventCatalogItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [sitemapText, setSitemapText] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>([]);
  const [messengers, setMessengers] = useState<ScheduleMessenger[]>(
    DEFAULT_SCHEDULE_MESSENGERS,
  );
  const [lastRunAt, setLastRunAt] = useState<string | null>(null);
  const [lastRunStatus, setLastRunStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ variant: string; text: string } | null>(
    null,
  );
  const [modalMessage, setModalMessage] = useState<{
    variant: string;
    text: string;
  } | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [resumeEditOnDeleteCancel, setResumeEditOnDeleteCancel] = useState(false);
  const pendingDeleteIdRef = useRef<string | null>(null);
  const openDeleteAfterEditExitRef = useRef(false);
  const resumeEditAfterDeleteExitRef = useRef(false);
  const [listIndexSchedules] = useLazyListIndexSchedulesQuery();
  const [createIndexSchedule] = useCreateIndexScheduleMutation();
  const [patchIndexSchedule] = usePatchIndexScheduleMutation();
  const [deleteIndexSchedule] = useDeleteIndexScheduleMutation();

  const loadForm = useCallback((item: IndexScheduleItem | null, asNew = false) => {
    if (asNew || !item) {
      setIsNew(true);
      setSelectedId(null);
      setName('زمان‌بندی جدید');
      setEnabled(true);
      setSitemapText('');
      setWebhookEvents([]);
      setMessengers([...DEFAULT_SCHEDULE_MESSENGERS]);
      setLastRunAt(null);
      setLastRunStatus(null);
      return;
    }
    setIsNew(false);
    setSelectedId(item.id);
    setName(item.name);
    setEnabled(item.enabled);
    setSitemapText((item.sitemapUrls ?? []).join('\n'));
    setWebhookEvents(item.webhookEvents ?? []);
    setMessengers(item.messengers ?? []);
    setLastRunAt(item.lastRunAt);
    setLastRunStatus(item.lastRunStatus);
  }, []);

  const openCreate = useCallback(() => {
    setModalMessage(null);
    loadForm(null, true);
    setModalOpen(true);
  }, [loadForm]);

  const openEdit = useCallback(
    (id: string) => {
      const item = schedules.find((row) => row.id === id);
      if (!item) return;
      setModalMessage(null);
      loadForm(item);
      setModalOpen(true);
    },
    [schedules, loadForm],
  );

  const closeModal = useCallback(() => {
    if (busy || deleteBusy) return;
    openDeleteAfterEditExitRef.current = false;
    setModalOpen(false);
    setModalMessage(null);
  }, [busy, deleteBusy]);

  const onEditModalExited = useCallback(() => {
    cleanupOrphanModalBackdrops();
    if (!openDeleteAfterEditExitRef.current) return;
    openDeleteAfterEditExitRef.current = false;
    setDeleteConfirmOpen(true);
  }, []);

  const onDeleteModalExited = useCallback(() => {
    cleanupOrphanModalBackdrops();
    if (!resumeEditAfterDeleteExitRef.current) return;
    resumeEditAfterDeleteExitRef.current = false;
    setModalOpen(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void listIndexSchedules(projectId, true)
      .unwrap()
      .then((data) => {
        if (cancelled) return;
        setSchedules(data.schedules);
        setCatalog(data.webhookEventCatalog);
      })
      .catch((err) => {
        if (cancelled) return;
        setMessage({
          variant: 'danger',
          text: getIndexerErrorInfo(err).message || 'بارگذاری ناموفق بود',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, listIndexSchedules]);

  const sitemapUrls = useMemo(() => normalizeLines(sitemapText), [sitemapText]);
  const withinDupes = useMemo(
    () => findDuplicateLines(sitemapUrls),
    [sitemapUrls],
  );
  const nonXmlUrls = useMemo(
    () => findNonXmlSitemapUrls(sitemapUrls),
    [sitemapUrls],
  );
  const localWarnings = useMemo(() => {
    const others = schedules
      .filter((item) => item.id !== selectedId)
      .map((item) => ({ name: item.name, items: item.sitemapUrls ?? [] }));
    return crossScheduleWarnings(sitemapUrls, others, 'سایت مپ');
  }, [sitemapUrls, schedules, selectedId]);
  const sitemapFormInvalid =
    withinDupes.length > 0 ||
    nonXmlUrls.length > 0 ||
    localWarnings.length > 0 ||
    (isNew && sitemapUrls.length === 0);

  const tableRows = useMemo(
    () =>
      schedules.map((item) => {
        const messengerSuffix = formatScheduleMessengersLabel(item.messengers ?? []);
        const messengerLabel = messengerSuffix ? ` · ${messengerSuffix}` : '';
        return {
          id: item.id,
          name: item.name,
          enabled: item.enabled,
          detail: `${formatFaNumber(item.sitemapUrls?.length ?? 0)} sitemap${messengerLabel}`,
          lastRunAt: item.lastRunAt,
          lastRunStatus: item.lastRunStatus,
        };
      }),
    [schedules],
  );

  async function onSave() {
    setBusy(true);
    setModalMessage(null);
    if (isNew && sitemapUrls.length === 0) {
      setModalMessage({
        variant: 'danger',
        text: 'حداقل یک آدرس sitemap وارد کنید.',
      });
      setBusy(false);
      return;
    }
    if (nonXmlUrls.length > 0) {
      setModalMessage({
        variant: 'danger',
        text: `آدرس sitemap باید به .xml ختم شود: ${nonXmlUrls.join('، ')}`,
      });
      setBusy(false);
      return;
    }
    if (withinDupes.length > 0) {
      setModalMessage({
        variant: 'danger',
        text: `آدرس sitemap تکراری در همین زمان‌بندی مجاز نیست: ${withinDupes.join('، ')}`,
      });
      setBusy(false);
      return;
    }
    if (localWarnings.length > 0) {
      setModalMessage({
        variant: 'danger',
        text: `سایت مپ قبلاً اضافه شده است. ${localWarnings.join(' ')}`,
      });
      setBusy(false);
      return;
    }
    try {
      const input = {
        name: name.trim(),
        enabled,
        sitemapUrls,
        webhookEvents,
        messengers: filterJoinedScheduleMessengers(messengers, userInfo),
      };
      const result = isNew
        ? await createIndexSchedule({ projectId, ...input }).unwrap()
        : await patchIndexSchedule({
            projectId,
            scheduleId: selectedId!,
            ...input,
          }).unwrap();
      setSchedules((prev) => {
        if (isNew) return [...prev, result.schedule];
        return prev.map((item) =>
          item.id === result.schedule.id ? result.schedule : item,
        );
      });
      setModalOpen(false);
      setMessage({
        variant: 'success',
        text: `زمان‌بندی «${result.schedule.name}» ذخیره شد.`,
      });
    } catch (err) {
      setModalMessage({
        variant: 'danger',
        text: formatRequestError(err, 'ذخیره ناموفق بود'),
      });
    } finally {
      setBusy(false);
    }
  }

  function requestDelete(id: string) {
    pendingDeleteIdRef.current = id;
    setDeleteTargetId(id);
    setResumeEditOnDeleteCancel(modalOpen && selectedId === id);
    resumeEditAfterDeleteExitRef.current = false;
    if (modalOpen) {
      openDeleteAfterEditExitRef.current = true;
      setModalOpen(false);
      return;
    }
    openDeleteAfterEditExitRef.current = false;
    setDeleteConfirmOpen(true);
  }

  function closeDeleteConfirm() {
    if (deleteBusy) return;
    const resumeEdit = resumeEditOnDeleteCancel;
    pendingDeleteIdRef.current = null;
    setDeleteTargetId(null);
    setResumeEditOnDeleteCancel(false);
    resumeEditAfterDeleteExitRef.current = Boolean(resumeEdit && selectedId);
    setDeleteConfirmOpen(false);
  }

  async function confirmDelete() {
    const targetId = pendingDeleteIdRef.current ?? deleteTargetId;
    if (!targetId) return;
    const targetName =
      schedules.find((item) => item.id === targetId)?.name ?? name;
    setDeleteBusy(true);
    setMessage(null);
    setModalMessage(null);
    try {
      await deleteIndexSchedule({ projectId, scheduleId: targetId }).unwrap();
      setSchedules((prev) => prev.filter((item) => item.id !== targetId));
      pendingDeleteIdRef.current = null;
      openDeleteAfterEditExitRef.current = false;
      resumeEditAfterDeleteExitRef.current = false;
      setResumeEditOnDeleteCancel(false);
      setModalOpen(false);
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
      setMessage({
        variant: 'success',
        text: `زمان‌بندی «${targetName}» حذف شد.`,
      });
    } catch (err) {
      openDeleteAfterEditExitRef.current = false;
      resumeEditAfterDeleteExitRef.current = false;
      setResumeEditOnDeleteCancel(false);
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
      pendingDeleteIdRef.current = null;
      setMessage({
        variant: 'danger',
        text: formatRequestError(err, 'حذف ناموفق بود'),
      });
    } finally {
      setDeleteBusy(false);
    }
  }

  if (loading) {
    return <SchedulePanelPreload />;
  }

  return (
    <>
      {message && (
        <IconAlert
          variant={indexerAlertVariant(message.variant)}
          dismissible
          onClose={() => setMessage(null)}
          className="mb-3"
        >
          <p className="mb-0 text-justify pe-3">{message.text}</p>
        </IconAlert>
      )}

      <IconAlert variant="info" className="mb-3">
        <p className="mb-0 text-justify pe-3">
          همه زمان‌بندی‌های <strong>فعال</strong> به‌صورت <strong>خودکار</strong> اجرا
          می‌شوند و از sitemap حداکثر <strong>۲۰۰ آدرس آخر</strong> خوانده می‌شود؛ فقط
          آدرس‌های <strong>تازه در sitemap (۲۴h)</strong> به گوگل می‌روند.
        </p>
      </IconAlert>

      <ScheduleListTable
        rows={tableRows}
        detailHeader="sitemap"
        emptyLabel="برای اجرای خودکار، یک زمان‌بندی با آدرس sitemap بسازید."
        onCreate={openCreate}
        onEdit={openEdit}
        onDelete={requestDelete}
      />

      <Modal
        show={modalOpen}
        onHide={closeModal}
        onExited={onEditModalExited}
        size="lg"
        scrollable
        centered
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs--1 fw-semibold mb-0">
            {isNew ? 'زمان‌بندی جدید — ایندکس sitemap' : `ویرایش: ${name}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isNew && (
            <IconAlert variant="info" className="mb-3">
              <p className="mb-0 text-justify pe-3">
                اینجا برای sitemap یک زمان‌بندی می‌سازید تا سیستم در بازه‌های منظم آن را
                بخواند و فقط آدرس‌های تازه (۲۴ ساعت اخیر در sitemap) را خودکار برای ایندکس
                به گوگل بفرستد.
              </p>
            </IconAlert>
          )}

          {modalMessage && (
            <IconAlert
              variant={indexerAlertVariant(modalMessage.variant)}
              className="mb-3"
              dismissible
              onClose={() => setModalMessage(null)}
            >
              <p className="mb-0 text-justify pe-3">{modalMessage.text}</p>
            </IconAlert>
          )}

          {localWarnings.length > 0 && (
            <IconAlert variant="danger" className="mb-3">
              <div className="text-justify pe-3">
                <div>سایت مپ قبلاً اضافه شده است.</div>
                {localWarnings.map((warning) => (
                  <div key={warning}>{warning}</div>
                ))}
              </div>
            </IconAlert>
          )}

          {nonXmlUrls.length > 0 && (
            <IconAlert variant="danger" className="mb-3">
              <p className="mb-0 text-justify pe-3">
                آدرس sitemap باید به <strong>.xml</strong> ختم شود:{' '}
                {nonXmlUrls.join('، ')}
              </p>
            </IconAlert>
          )}

          {withinDupes.length > 0 && (
            <IconAlert variant="danger" className="mb-3">
              <p className="mb-0 text-justify pe-3">
                آدرس sitemap تکراری در همین زمان‌بندی مجاز نیست:{' '}
                {withinDupes.join('، ')}
              </p>
            </IconAlert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>نام زمان‌بندی</Form.Label>
            <Form.Control value={name} onChange={(e) => setName(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>آدرس sitemapها (هر خط یک آدرس، با پسوند .xml)</Form.Label>
            <Form.Control
              as="textarea"
              className="url-textarea mono"
              rows={5}
              placeholder={projectSitemapListPlaceholder(project)}
              value={sitemapText}
              onChange={(e) => setSitemapText(e.target.value)}
              isInvalid={
                nonXmlUrls.length > 0 ||
                withinDupes.length > 0 ||
                localWarnings.length > 0
              }
            />
          </Form.Group>

          <ScheduleWebhookEventsForm
            title="رویدادها"
            catalog={catalog}
            events={webhookEvents}
            onChange={setWebhookEvents}
          />

          <ScheduleMessengerSelect value={messengers} onChange={setMessengers} />

          {!isNew && lastRunAt && (
            <p className="text-secondary small mb-0">
              آخرین اجرا: {formatTime(lastRunAt)}
              {lastRunStatus
                ? ` — ${jobStateLabel(lastRunStatus as ProjectJobState)}`
                : ''}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className="pt-3 d-flex flex-wrap align-items-center gap-2">
          {!isNew && selectedId && (
            <Button
              type="button"
              variant="outline-danger"
              disabled={busy}
              onClick={() => requestDelete(selectedId)}
            >
              حذف
            </Button>
          )}
          <div className="d-flex align-items-center flex-wrap gap-2 gap-md-3 ms-auto">
            <Form.Check
              type="switch"
              id="index-schedule-enabled"
              className="schedule-modal-enabled-switch mb-0"
              label="فعال (خودکار اجرا می‌شود)"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <Button variant="outline-secondary" disabled={busy} onClick={closeModal}>
              انصراف
            </Button>
            <Button
              variant="falcon-primary"
              disabled={busy || !name.trim() || sitemapFormInvalid}
              onClick={() => void onSave()}
            >
              {busy && (
                <Spinner
                  size="sm"
                  animation="border"
                  className="me-2"
                  style={{ width: 12, height: 12 }}
                />
              )}
              {isNew ? 'ساخت' : 'ذخیره'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ScheduleDeleteConfirmModal
        show={deleteConfirmOpen}
        scheduleName={
          schedules.find((item) => item.id === deleteTargetId)?.name ?? '—'
        }
        busy={deleteBusy}
        onConfirm={() => void confirmDelete()}
        onClose={closeDeleteConfirm}
        onExited={onDeleteModalExited}
      />
    </>
  );
}
